import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, message } from 'antd';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { Button, Skeleton } from '@admin-panel/ui';
import LookContentEditor, {
  type LookContentFormValues,
} from '../../../../components/LookContentEditor';
import { OccasionPdpContent } from '../../../../components/LookPdpContent';
import { EditSectionDialog } from '../../../StylingJobs/StylingWorkflowDetailDrawer/WorkflowRunItem/components/EditSectionDialog';
import {
  buildLookEditorMetadata,
  normalizeLookMetadata,
  type LookMetadata,
} from '../../../../components/lookMetadata';
import type { LookDTOType, LookGroupLookDTOType } from '../../../../schemas/styling';
import {
  useGetLookByIdQuery,
  useLazyGetLookByIdQuery,
  usePublishLookMutation,
  useGenerateVisualSearchMutation,
  useUnpublishLookMutation,
  useUpdateLookContentMutation,
} from '../../../../store/stylist/stylistApi';
import { pollLookUntilVisualSearchReady } from '../../../../components/visualSearchPolling';
import {
  deepMergeRecord,
  type EditableSection,
  normalizeWorkflowRecommendations,
} from '../../../StylingJobs/StylingWorkflowDetailDrawer/WorkflowRunItem/helpers';

type LookEditorPaneProps = {
  lookId: number;
  onClose?: () => void;
  onLookUpdated: (look: LookDTOType) => void;
  showCloseControls?: boolean;
  showPublishControls?: boolean;
  embedded?: boolean;
  enabled?: boolean;
};

const hasBackendErrorKey = (error: unknown, key: string) => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const data = 'data' in error && error.data && typeof error.data === 'object' ? error.data : null;
  const status =
    data && 'status' in data && data.status && typeof data.status === 'object' ? data.status : null;

  return (status && 'key' in status ? status.key : '') === key;
};

const toGroupLookSummary = (look: LookDTOType): LookGroupLookDTOType => ({
  id: look.id,
  group_display_order: look.group_display_order,
  is_active: look.is_active,
  qc_status: look.qc_status,
  image_generation_status: look.image_generation_status,
  look_name: look.look_name,
  look_hook: look.look_hook,
  created_on: look.created_on,
  generated_image_url: look.generated_image_url,
  source_image_url: look.source_image_url,
  tagged_product_count: 0,
  tagged_products: [],
});

export { toGroupLookSummary };

const LookEditorPane = ({
  lookId,
  onClose,
  onLookUpdated,
  showCloseControls = true,
  showPublishControls = true,
  embedded = false,
  enabled = true,
}: LookEditorPaneProps) => {
  const editorPortalRef = useRef<HTMLDivElement | null>(null);
  const [form] = Form.useForm<LookContentFormValues>();
  const [editForm] = Form.useForm<LookContentFormValues>();
  const [editingLook, setEditingLook] = useState<LookDTOType | null>(null);
  const [editingSection, setEditingSection] = useState<EditableSection>(null);
  const [isPollingVisualSearch, setIsPollingVisualSearch] = useState(false);

  const { currentData, isLoading, isFetching } = useGetLookByIdQuery(lookId, {
    skip: !enabled,
  });
  const [triggerGetLookById] = useLazyGetLookByIdQuery();
  const [updateLookContent, { isLoading: isUpdating }] = useUpdateLookContentMutation();
  const [publishLook, { isLoading: isPublishing }] = usePublishLookMutation();
  const [unpublishLook, { isLoading: isUnpublishing }] = useUnpublishLookMutation();
  const [generateVisualSearch, { isLoading: isTriggeringVisualSearch }] =
    useGenerateVisualSearchMutation();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    setEditingLook(null);
  }, [enabled, lookId]);

  useEffect(() => {
    if (currentData) {
      setEditingLook(currentData);
    }
  }, [currentData]);

  const currentLook = editingLook ?? currentData ?? null;
  const isPublished = currentLook?.qc_status === 'published';
  const saveMessageKey = `look-editor-save-${lookId}`;
  const publishMessageKey = `look-editor-publish-${lookId}`;
  const enrichmentMessageKey = `look-editor-enrichment-${lookId}`;

  const recommendationDefaults = useMemo(
    () => normalizeWorkflowRecommendations(currentLook?.product_recommendations),
    [currentLook?.product_recommendations],
  );

  const applyLookToForm = (
    targetForm: ReturnType<typeof Form.useForm<LookContentFormValues>>[0],
    look: LookDTOType,
  ) => {
    targetForm.resetFields();
    targetForm.setFieldsValue({
      occasion_name: look.occasion_name || '',
      sub_occasion_name: look.sub_occasion_name || '',
      look_name: look.look_name || '',
      look_hook: look.look_hook || '',
      description: look.description || '',
      scene_description: look.scene_description || '',
      layering_type: look.layering_type || 'with_layering',
      blurb: look.blurb || '',
      search_seeds: Array.isArray(look.search_seeds) ? look.search_seeds : [],
      product_recommendations: recommendationDefaults,
      metadata: buildLookEditorMetadata(look as unknown as Record<string, unknown>),
    });
  };

  useEffect(() => {
    if (!currentLook) {
      return;
    }

    applyLookToForm(form, currentLook);
  }, [currentLook, form, recommendationDefaults]);

  const handleSave = async () => {
    if (!currentLook) {
      return false;
    }

    try {
      const values = (await form.validateFields()) as LookContentFormValues;
      const recommendationsForSave =
        (form.getFieldValue(
          'product_recommendations',
        ) as LookContentFormValues['product_recommendations']) || recommendationDefaults;

      const mergedRecommendations = recommendationsForSave.map((recommendation, index) => {
        const existingRecommendation = recommendationDefaults[index];
        return {
          product_type: recommendation.product_type || existingRecommendation?.product_type || '',
          description: recommendation.description || existingRecommendation?.description || '',
          mapped_lk_styles:
            recommendation.mapped_lk_styles?.map(value => String(value)) ||
            existingRecommendation?.mapped_lk_styles ||
            [],
          mapped_product_images:
            recommendation.mapped_product_images ||
            existingRecommendation?.mapped_product_images ||
            {},
        };
      });

      const mergedMetadata = deepMergeRecord(
        buildLookEditorMetadata(
          currentLook as unknown as Record<string, unknown>,
        ) as unknown as Record<string, unknown>,
        (values.metadata ?? {}) as unknown as Record<string, unknown>,
      );
      const metadataPayload = normalizeLookMetadata(mergedMetadata as unknown as LookMetadata);

      message.loading({
        key: saveMessageKey,
        content: 'Saving look content...',
        duration: 0,
      });

      const updatedLook = await updateLookContent({
        id: currentLook.id,
        data: {
          occasion_name: values.occasion_name ?? currentLook.occasion_name,
          sub_occasion_name: values.sub_occasion_name ?? currentLook.sub_occasion_name ?? undefined,
          look_name: values.look_name ?? currentLook.look_name ?? undefined,
          look_hook: values.look_hook ?? currentLook.look_hook ?? undefined,
          description: values.description ?? currentLook.description ?? undefined,
          scene_description: values.scene_description ?? currentLook.scene_description ?? undefined,
          layering_type: values.layering_type ?? currentLook.layering_type,
          blurb: values.blurb ?? currentLook.blurb,
          search_seeds: values.search_seeds ?? (Array.isArray(currentLook.search_seeds) ? currentLook.search_seeds : []),
          product_recommendations: mergedRecommendations,
          metadata: metadataPayload,
        },
      }).unwrap();

      setEditingLook(updatedLook);
      onLookUpdated(updatedLook);
      message.success({
        key: saveMessageKey,
        content: 'Look content updated',
      });
      return true;
    } catch (error) {
      console.error('Error updating look content:', error);
      message.error({
        key: saveMessageKey,
        content: 'Failed to update look content',
      });
      return false;
    }
  };

  const setEditFormDefaults = () => {
    if (!currentLook) {
      return;
    }

    applyLookToForm(editForm, currentLook);
  };

  const handleEditSection = (section: EditableSection) => {
    if (!section) {
      return;
    }

    setEditFormDefaults();
    setEditingSection(section);
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    editForm.resetFields();
  };

  const handleResetEdit = () => {
    setEditFormDefaults();
  };

  const handleResetEnrichment = () => {
    if (!currentLook) {
      return;
    }

    applyLookToForm(form, currentLook);
  };

  const handleSaveSection = async () => {
    if (!currentLook || !editingSection) {
      return;
    }

    try {
      const values = (await editForm.validateFields()) as LookContentFormValues;
      const mergedMetadata = deepMergeRecord(
        buildLookEditorMetadata(
          currentLook as unknown as Record<string, unknown>,
        ) as unknown as Record<string, unknown>,
        (values.metadata ?? {}) as unknown as Record<string, unknown>,
      );
      const metadataPayload = normalizeLookMetadata(mergedMetadata as unknown as LookMetadata);

      message.loading({
        key: saveMessageKey,
        content: 'Saving section...',
        duration: 0,
      });

      const updatedLook = await updateLookContent({
        id: currentLook.id,
        data: {
          occasion_name: values.occasion_name ?? currentLook.occasion_name,
          sub_occasion_name: values.sub_occasion_name ?? currentLook.sub_occasion_name ?? undefined,
          look_name: values.look_name ?? currentLook.look_name ?? undefined,
          look_hook: values.look_hook ?? currentLook.look_hook ?? undefined,
          description: values.description ?? currentLook.description ?? undefined,
          scene_description: values.scene_description ?? currentLook.scene_description ?? undefined,
          layering_type: values.layering_type ?? currentLook.layering_type,
          blurb: values.blurb ?? currentLook.blurb,
          search_seeds: values.search_seeds ?? currentLook.search_seeds,
          product_recommendations: recommendationDefaults,
          metadata: metadataPayload,
        },
      }).unwrap();

      setEditingLook(updatedLook);
      onLookUpdated(updatedLook);
      setEditingSection(null);
      message.success({
        key: saveMessageKey,
        content: 'Section updated',
      });
    } catch (error) {
      console.error('Error updating look section:', error);
      message.error({
        key: saveMessageKey,
        content: 'Failed to update section',
      });
    }
  };

  const handleTogglePublish = async () => {
    if (!currentLook) {
      return;
    }

    try {
      message.loading({
        key: publishMessageKey,
        content: isPublished ? 'Unpublishing look...' : 'Publishing look...',
        duration: 0,
      });

      const updatedLook = isPublished
        ? await unpublishLook({ id: currentLook.id }).unwrap()
        : await publishLook({ id: currentLook.id }).unwrap();

      const nextLook = {
        ...currentLook,
        qc_status: updatedLook.qc_status,
        qc_at: updatedLook.qc_at,
        published_at: updatedLook.published_at,
      };

      setEditingLook(nextLook);
      onLookUpdated(nextLook);
      message.success({
        key: publishMessageKey,
        content: isPublished ? 'Look unpublished' : 'Look published',
      });

      if (updatedLook.qc_status !== 'published' && onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error toggling publish state:', error);
      message.error({
        key: publishMessageKey,
        content: isPublished ? 'Failed to unpublish look' : 'Failed to publish look',
      });
    }
  };

  const handleTriggerVisualSearch = async () => {
    if (!currentLook || currentLook.qc_status !== 'published') {
      message.warning('Publish the look before generating visual-search source images.');
      return;
    }

    try {
      message.loading({
        key: enrichmentMessageKey,
        content: 'Generating visual-search source images...',
        duration: 0,
      });

      const updatedLook = await generateVisualSearch({
        id: currentLook.id,
      }).unwrap();

      setEditingLook(updatedLook);
      onLookUpdated(updatedLook);
      setIsPollingVisualSearch(true);
      const pollResult = await pollLookUntilVisualSearchReady({
        lookId: currentLook.id,
        fetchLookById: id => triggerGetLookById(id).unwrap(),
        onLookUpdated: nextLook => {
          setEditingLook(nextLook);
          onLookUpdated(nextLook);
        },
      });

      if (pollResult.status === 'ready') {
        message.success({
          key: enrichmentMessageKey,
          content: 'Visual-search source images generated',
        });
        return;
      }

      if (pollResult.status === 'failed') {
        message.error({
          key: enrichmentMessageKey,
          content: 'Visual-search source image generation failed',
        });
        return;
      }
    } catch (error) {
      console.error('Error triggering visual search:', error);
      if (hasBackendErrorKey(error, 'LOOK_VISUAL_SEARCH_ALREADY_GENERATED')) {
        message.warning({
          key: enrichmentMessageKey,
          content: 'Visual-search source images have already been generated for this look',
        });
        return;
      }
      message.error({
        key: enrichmentMessageKey,
        content: 'Failed to generate visual-search source images',
      });
    } finally {
      setIsPollingVisualSearch(false);
    }
  };

  const handleRefreshVisualSearch = async () => {
    if (!currentLook) {
      return;
    }

    try {
      setIsPollingVisualSearch(true);
      const pollResult = await pollLookUntilVisualSearchReady({
        lookId: currentLook.id,
        fetchLookById: id => triggerGetLookById(id).unwrap(),
        onLookUpdated: nextLook => {
          setEditingLook(nextLook);
          onLookUpdated(nextLook);
        },
      });

      if (pollResult.status === 'ready') {
        message.success({
          key: enrichmentMessageKey,
          content: 'Visual-search source images generated',
        });
        return;
      }

      if (pollResult.status === 'failed') {
        message.error({
          key: enrichmentMessageKey,
          content: 'Visual-search source image generation failed',
        });
      }
    } catch (error) {
      console.error('Error refreshing visual search:', error);
      message.error({
        key: enrichmentMessageKey,
        content: 'Failed to refresh visual-search status',
      });
    } finally {
      setIsPollingVisualSearch(false);
    }
  };

  const handleVisualSearchUpdated = (updatedLook: LookDTOType) => {
    setEditingLook(updatedLook);
    onLookUpdated(updatedLook);
  };

  const handleSearchSeedsUpdated = (updatedLook: LookDTOType) => {
    setEditingLook(updatedLook);
    onLookUpdated(updatedLook);
  };

  if ((isLoading || isFetching) && !currentLook) {
    return (
      <div
        className={[
          embedded
            ? 'rounded-sm bg-background'
            : 'h-full overflow-y-auto rounded-md border border-border/80 bg-background',
        ].join(' ')}
      >
        <div className="space-y-3 border-b border-border/80 p-4">
          <Skeleton className="h-5 w-32 rounded-sm" />
          <Skeleton className="h-4 w-56 rounded-sm" />
        </div>
        <div className="grid gap-4 p-4 lg:grid-cols-[220px_minmax(0,1fr)]">
          <Skeleton className="aspect-2/3 w-full rounded-sm" />
          <div className="space-y-3">
            <Skeleton className="h-10 w-full rounded-sm" />
            <Skeleton className="h-24 w-full rounded-sm" />
            <Skeleton className="h-24 w-full rounded-sm" />
            <Skeleton className="h-24 w-full rounded-sm" />
          </div>
        </div>
      </div>
    );
  }

  if (!enabled && !currentLook) {
    return (
      <div className="flex min-h-72 items-center justify-center rounded-sm bg-background text-sm text-muted-foreground">
        Scroll to load look details
      </div>
    );
  }

  if (!currentLook) {
    return (
      <div
        className={[
          'flex min-h-0 items-center justify-center rounded-md bg-background text-sm text-muted-foreground',
          embedded ? 'min-h-56' : 'h-full',
        ].join(' ')}
      >
        Could not load look details.
      </div>
    );
  }

  return (
    <div
      ref={editorPortalRef}
      className={embedded ? 'min-w-0 bg-background' : 'h-full overflow-y-auto bg-background'}
    >
      {showCloseControls || showPublishControls ? (
        <div className="flex items-start justify-between gap-4 px-4 py-3">
          <div className="flex shrink-0 items-center gap-2">
            {showCloseControls ? (
              <Button
                type="button"
                size="small"
                variant="outline"
                icon={<ArrowLeftIcon size={14} />}
                onClick={onClose}
              >
                Back
              </Button>
            ) : null}
            {showPublishControls ? (
              <Button
                type="button"
                size="small"
                variant={isPublished ? 'outline' : 'solid'}
                isLoading={isPublishing || isUnpublishing}
                onClick={handleTogglePublish}
              >
                {isPublished ? 'Unpublish' : 'Publish'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={embedded ? 'space-y-2' : 'space-y-5 px-4 py-3'}>
        <OccasionPdpContent
          look={currentLook}
          canEdit
          canEditOccasion={false}
          onEditSection={handleEditSection}
          visibleSections={{
            occasion: true,
            look_name: true,
            website: true,
            pdp: false,
            search_seeds: false,
            visual_search: false,
          }}
        />

        <Form form={form} layout="vertical">
          <LookContentEditor
            form={form}
            mode="full"
            presentation="flat"
            showMetadata
            baseProductColor={currentLook.base_product_color}
            initialProductRecommendations={recommendationDefaults}
            initialSearchSeeds={
              Array.isArray(currentLook.search_seeds) ? currentLook.search_seeds : []
            }
            productPickerPortalContainer={editorPortalRef.current}
            widgets={currentLook.widgets}
            visualSearch={currentLook.visual_search}
            lookId={currentLook.id}
            isPublished={isPublished}
            isTriggeringVisualSearch={isTriggeringVisualSearch || isPollingVisualSearch}
            onTriggerVisualSearch={handleTriggerVisualSearch}
            onRefreshVisualSearch={handleRefreshVisualSearch}
            onVisualSearchUpdated={handleVisualSearchUpdated}
            onSearchSeedsUpdated={handleSearchSeedsUpdated}
            scrollContainer={embedded ? null : editorPortalRef.current}
            compactEnrichments
            sections={['pdp', 'visual_search', 'search_seeds']}
            onSaveEnrichmentSection={handleSave}
            onResetEnrichmentSection={() => handleResetEnrichment()}
            isSavingEnrichmentSection={isUpdating}
          />
        </Form>
      </div>

      {editingSection ? (
        <EditSectionDialog
          editingSection={editingSection}
          editForm={editForm}
          recommendationDefaults={recommendationDefaults}
          portalContainer={editorPortalRef.current}
          isUpdating={isUpdating}
          onCancel={handleCancelEdit}
          onReset={handleResetEdit}
          onSave={handleSaveSection}
        />
      ) : null}
    </div>
  );
};

export default LookEditorPane;
